<template>
  <div class="card">
    <div class="card-header">
      <div class="card-header-title">
        <div class="title is-6 is-marginless">
          Card Link Lists <span class="tag">Προσθήκη</span>
        </div>
      </div>
      <div class="card-header-icon">
        <RouterLink
          :to="{ name: 'content.home.card-link-lists.list' }"
          class="button"
        >
          <span class="icon is-small"><i class="fa fa-list"/></span>
          <span>Λίστα</span>
        </RouterLink>
      </div>
    </div>

    <form @submit.prevent="submit">
      <div class="card-content">
        <!-- Row 1: Titles -->
        <div class="columns is-multiline">
          <div class="column is-half">
            <div class="field">
              <label class="label">Τίτλος (EL) *</label>
              <div class="control">
                <input
                  v-validate="'required'"
                  v-model.trim="form.title_el"
                  class="input"
                  type="text"
                  name="title_el"
                />
                <p v-show="errors.has('title_el')" class="help is-danger">
                  Εισάγετε τίτλο στα Ελληνικά
                </p>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="field">
              <label class="label">Τίτλος (EN) *</label>
              <div class="control">
                <input
                  v-validate="'required'"
                  v-model.trim="form.title_en"
                  class="input"
                  type="text"
                  name="title_en"
                />
                <p v-show="errors.has('title_en')" class="help is-danger">
                  Εισάγετε τίτλο στα Αγγλικά
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 2: Order -->
        <div class="columns">
          <div class="column is-one-third">
            <div class="field">
              <label class="label">Σειρά εμφάνισης</label>
              <div class="control">
                <input
                  v-model.number="form.order"
                  class="input"
                  type="number"
                  min="0"
                  step="1"
                  name="order"
                />
              </div>
              <p class="help">
                Χρησιμοποιείται για την ταξινόμηση λιστών (0, 1, 2, ...).
              </p>
            </div>
          </div>
        </div>

        <!-- Row 3: Active toggle (full width, its own row) -->
        <div class="columns">
          <div class="column is-full">
            <div class="field">
              <label class="label" title="Μέγιστο 3 ενεργές λίστες">
                Εμφανής *
              </label>
              <div class="control">
                <ToggleButton
                  :sync="true"
                  :value="form.is_active"
                  :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                  :width="30"
                  :height="15"
                  @change="updateActive"
                />
              </div>
              <p class="help">Μέγιστο 3 ενεργές λίστες επιτρέπονται.</p>
            </div>
          </div>
        </div>

        <!-- Row 4: After-save checkbox (full width, its own row) -->
        <div class="columns">
          <div class="column is-full">
            <div class="field">
              <label
                class="checkbox"
                title="Μετάβαση στις κάρτες αυτής της λίστας"
              >
                <input v-model="goToItemsAfterSave" type="checkbox" />
                Μετάβαση στις κάρτες μετά την αποθήκευση
              </label>
              <p class="help">
                Αν επιλεγεί, μετά την αποθήκευση θα ανοίξει η σελίδα διαχείρισης
                καρτών της νέας λίστας για προσθήκη στοιχείων.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <FormSubmit :is-saving="isSaving" @reset="reset" />
      </div>
    </form>
  </div>
</template>

<script>
import request from '@/utils/request';
import FormSubmit from '@/views/components/FormSubmit';

export default {
  name: 'CardLinkListsAdd',
  components: { FormSubmit },

  data() {
    return {
      form: {
        title_el: '',
        title_en: '',
        is_active: false,
        order: 0,
      },
      goToItemsAfterSave: false,
      isSaving: false,
    };
  },

  methods: {
    updateActive({ value }) {
      this.form.is_active = !!value;
    },

    async submit() {
      try {
        const ok = await this.$validator.validateAll();
        if (!ok) throw new Error('Client-side validation failed');

        this.isSaving = true;

        const payload = {
          title_el: this.form.title_el,
          title_en: this.form.title_en,
          is_active: this.form.is_active,
          order: Number.isFinite(this.form.order) ? this.form.order : 0,
        };

        const { data, status } = await request.post(
          '/home/card-link-lists',
          payload,
        );

        this.isSaving = false;

        if (status !== 201) {
          throw new Error(data?.message || 'To αίτημα απέτυχε');
        }

        // Successful create
        const createdId = data?.id || data?.data?.id;

        // Navigate either back to lists or straight to items manager
        if (this.goToItemsAfterSave && createdId) {
          this.$router
            .push({
              name: 'content.home.card-link-lists.items',
              params: { id: createdId },
            })
            .catch(() => {});
        } else {
          this.$router
            .push({ name: 'content.home.card-link-lists.list' })
            .catch(() => {});
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η λίστα αποθηκεύτηκε',
        });
      } catch (err) {
        this.isSaving = false;
        const msg =
          err?.response?.data?.message || err?.message || 'To αίτημα απέτυχε';
        const uiMsg =
          msg.includes('up to 3') || msg.includes('Maximum of 3')
            ? 'Μέγιστο 3 ενεργές λίστες'
            : msg;

        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: uiMsg,
        });
      }
    },

    reset() {
      this.form = {
        title_el: '',
        title_en: '',
        is_active: false,
        order: 0,
      };
      this.goToItemsAfterSave = false;
      this.$validator.reset();
    },
  },
};
</script>
